'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const timeline = [
{
  year: '2020',
  title: 'Company Founded',
  desc: 'AGILAS SYSTEMS LLP established in Rajkot, Gujarat with a vision to revolutionize lithium battery manufacturing in India.',
  gwh: '0.1 GWh',
  active: false
},
{
  year: '2022',
  title: 'BESS & Industrial Storage',
  desc: 'Launched lithium drop-in replacements for UPS & inverters. Deployed first 100+ industrial battery packs.',
  gwh: '0.5 GWh',
  active: false
},
{
  year: '2024',
  title: 'BMS & Custom Packs',
  desc: 'Expanded into custom BMS firmware, cell selection services, and large-format pack manufacturing.',
  gwh: '1 GWh',
  active: true
},
{
  year: '2026',
  title: 'Utility-Scale BESS',
  desc: 'Large-scale storage with AI optimization and grid-interactive systems for commercial and industrial clients.',
  gwh: '3 GWh',
  active: false
},
{
  year: '2030',
  title: 'Cell Manufacturing',
  desc: 'In-house lithium cell production facility. Complete vertical integration from cell to system.',
  gwh: '10 GWh',
  active: false
}];


const values = [
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>,

  title: 'Quality First',
  desc: 'We source only from Tier-1 cell manufacturers with rigorous quality control, certification, and 100% incoming inspection.'
},
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>,

  title: 'Technical Expertise',
  desc: 'Deep domain knowledge in battery chemistry, BMS configuration, cell matching, and energy storage system design.'
},
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>,

  title: 'Partnership Focused',
  desc: 'Building long-term relationships through transparent communication, reliable delivery, and dedicated after-sales support.'
},
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>,

  title: 'Clean Energy Mission',
  desc: 'Empowering India\'s transition to sustainable energy through accessible, reliable, and affordable lithium storage solutions.'
}];


const team = [
{
  name: 'Roshan Radadiya',
  role: 'Founder & CEO',
  exp: 'Electrical Engineer',
  skills: [
  'Startup ecosystem & venture building',
  'Strategic business development',
  'B2B supply chain & distribution',
  'Technology & product strategy'],

  img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
  alt: 'Roshan Radadiya - Founder & CEO of AGILAS SYSTEMS'
},
{
  name: 'Co-Founder & CTO',
  role: 'Technical Leadership',
  exp: '8+ Years in Battery Tech',
  skills: [
  'Lithium battery pack assembly & design',
  'BMS configuration & firmware',
  'Cell chemistry & electrochemistry',
  'Quality control & certification'],

  img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
  alt: 'Technical engineer and battery technology expert representing CTO role'
}];


export default function AboutPage() {
  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right');
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('visible');});},
      { threshold: 0.1 }
    );
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 circuit-bg opacity-30" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(230,0,0,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="section-label mb-6">About AGILAS SYSTEMS</div>
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              About <span className="gradient-text-green">AGILAS SYSTEMS</span>
            </h1>
            <p className="text-slate-500 text-xl leading-relaxed mb-8">
              Precision Lithium Battery Manufacturing. Intelligent Energy Solutions.
            </p>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                <strong className="text-slate-900">AGILAS SYSTEMS LLP</strong> is a Rajkot-based lithium battery manufacturer
                building high-performance energy storage for industrial and commercial applications. We design, assemble,
                and deploy lithium battery packs — from compact UPS replacements to large-scale BESS — with integrated
                BMS and full technical support.
              </p>
              <p>
                Our hardware is engineered as a{' '}
                <span className="text-primary font-medium">drop-in replacement</span> for traditional lead-acid batteries,
                while our BMS expertise provides{' '}
                <span className="text-primary font-medium">intelligent protection, monitoring, and optimization</span>{' '}
                that battery companies need to scale professionally.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Stats */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
            { val: '200+', label: 'Projects Delivered' },
            { val: '5000+', label: 'kWh Deployed' },
            { val: '5 Yrs', label: 'Warranty on Packs' },
            { val: '6000+', label: 'Cycle Life (LFP)' }]?.
            map((s, i) =>
            <div key={i} className="text-center animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-3xl font-bold text-primary mb-1">{s?.val}</div>
                <div className="text-slate-500 text-sm">{s?.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Team */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Leadership</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Meet Our <span className="gradient-text-green">Founders</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team?.map((member, i) =>
            <div key={i} className="card-dark p-8 animate-on-scroll" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="flex items-start gap-5 mb-6">
                  <img
                  src={member?.img}
                  alt={member?.alt}
                  className="w-20 h-20 rounded-2xl object-cover border border-slate-200" />
                
                  <div>
                    <h3 className="text-slate-900 font-bold text-lg">{member?.name}</h3>
                    <div className="text-primary text-sm font-medium">{member?.role}</div>
                    <div className="text-slate-500 text-xs mt-1">{member?.exp}</div>
                  </div>
                </div>
                <ul className="space-y-2">
                  {member?.skills?.map((skill, j) =>
                <li key={j} className="flex items-center gap-2 text-slate-600 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {skill}
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Our Journey</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Roadmap to <span className="gradient-text-green">2030</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Starting with industrial lithium storage, expanding into BMS and custom packs, building towards utility-scale BESS and cell manufacturing.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-transparent hidden md:block" />

            <div className="space-y-12">
              {timeline?.map((item, i) =>
              <div
                key={i}
                className={`flex flex-col md:flex-row items-center gap-8 animate-on-scroll ${
                i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`
                }
                style={{ animationDelay: `${i * 0.1}s` }}>
                
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="card-dark p-6 inline-block w-full md:max-w-sm">
                      <div className="text-primary font-bold text-2xl mb-1">{item?.year}</div>
                      <h3 className="text-slate-900 font-bold text-lg mb-2">{item?.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{item?.desc}</p>
                      <div className="mt-3 text-xs text-slate-400">Target: {item?.gwh}</div>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden md:flex flex-col items-center">
                    <div
                    className={`w-5 h-5 rounded-full border-2 ${
                    item?.active ?
                    'bg-primary border-primary shadow-[0_0_15px_rgba(14,165,233,0.4)]' :
                    'bg-white border-slate-300'}`
                    } />
                  
                  </div>

                  <div className="flex-1" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card-dark p-8 animate-on-scroll-left">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-6">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                To be India&apos;s most reliable manufacturer of industrial lithium battery systems. Hardware that never fails,
                BMS that always protects, and service that never stops.
              </p>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-slate-500 text-sm font-medium mb-3">What We Do Today:</p>
                <ul className="space-y-2">
                  {[
                  'Industrial lithium UPS and inverter battery packs',
                  'BMS configuration and integration services',
                  'Custom battery pack design and manufacturing']?.
                  map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {item}
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="card-dark p-8 animate-on-scroll-right">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-6">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                To build a complete energy ecosystem: from industrial lithium storage and intelligent BMS, to utility-scale
                BESS and in-house cell manufacturing by 2030.
              </p>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-slate-500 text-sm font-medium mb-3">Where We&apos;re Heading:</p>
                <ul className="space-y-2">
                  {[
                  'Utility-scale BESS with AI optimization',
                  'Full production and lifecycle software suite',
                  'In-house lithium cell manufacturing']?.
                  map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-400 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {item}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Core Values</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our <span className="gradient-text-green">Values</span>
            </h2>
            <p className="text-slate-600">The principles that guide everything we build and deliver.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((v, i) =>
            <div key={i} className="card-dark p-6 animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4">
                  {v?.icon}
                </div>
                <h3 className="text-slate-900 font-bold mb-2">{v?.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{v?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(230,0,0,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10 animate-on-scroll">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Partner With AGILAS SYSTEMS</h2>
          <p className="text-slate-600 mb-8">
            Whether you need industrial lithium storage, BMS configuration, or custom battery packs, we are ready to help.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/contact" className="btn-primary text-base px-8 py-3">
              Get in Touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link href="/bess-ess" className="btn-secondary text-base px-8 py-3">
              View Storage Solutions
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

}